import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface TeamOption {
  id: string;
  name: string;
}

/**
 * Searchable team picker. Shows the selected team's name as a button;
 * tapping opens a fixed-position modal overlay with a search input and
 * a scrollable list. Modal pattern (vs. inline absolute dropdown) avoids
 * mobile pitfalls: clipping by overflow ancestors, scroll-trapping, and
 * iOS Safari layout glitches.
 *
 * Emits a `team-change` CustomEvent with `detail.id` when the selection changes.
 */
@customElement('team-picker')
export class TeamPicker extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Array }) teams: TeamOption[] = [];
  @property({ type: String }) value = '';
  @property({ type: String }) placeholder = 'Pick a team…';
  @property({ type: Boolean }) error = false;
  @property({ type: Boolean }) disabled = false;
  /** When true, the picker styles itself as the "active" choice (e.g., the user's home team). */
  @property({ type: Boolean }) highlight = false;
  @property({ type: String }) testid = '';

  @state() private open = false;
  @state() private query = '';

  /**
   * Snapshot of `document.body.style.overflow` before we locked scrolling.
   * `null` means we are not currently holding a lock. We restore the prior
   * value (rather than blanking it) so we don't accidentally release a parent
   * modal's body-scroll lock when this picker closes.
   */
  private previousBodyOverflow: string | null = null;

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.open) {
      e.stopPropagation();
      this.close();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    this.unlockBodyScroll();
    super.disconnectedCallback();
  }

  private get selectedName(): string {
    return this.teams.find(t => t.id === this.value)?.name ?? '';
  }

  private lockBodyScroll() {
    if (this.previousBodyOverflow !== null) return; // already locked by us
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll() {
    if (this.previousBodyOverflow === null) return; // we never locked
    document.body.style.overflow = this.previousBodyOverflow;
    this.previousBodyOverflow = null;
  }

  private openModal() {
    if (this.disabled || this.open) return;
    this.open = true;
    this.query = '';
    this.lockBodyScroll();
    // Focus the search input on next paint.
    requestAnimationFrame(() => {
      const input = this.querySelector<HTMLInputElement>('input.team-picker-search');
      input?.focus();
    });
  }

  private close() {
    this.open = false;
    this.unlockBodyScroll();
  }

  private select(id: string) {
    this.value = id;
    this.close();
    this.dispatchEvent(new CustomEvent('team-change', { detail: { id }, bubbles: true, composed: true }));
  }

  render() {
    const filtered = this.query.trim()
      ? this.teams.filter(t => t.name.toLowerCase().includes(this.query.trim().toLowerCase()))
      : this.teams;

    return html`
      <button
        type="button"
        class="select select-bordered bg-base-100 w-full text-left flex items-center justify-between gap-2 ${this.error ? 'select-error' : ''} ${this.highlight ? 'ring-2 ring-primary/30' : ''}"
        ?disabled=${this.disabled}
        data-testid=${this.testid || nothing}
        @click=${this.openModal}
      >
        <span class="truncate ${this.selectedName ? '' : 'text-base-content/50'}">
          ${this.selectedName || this.placeholder}
        </span>
      </button>

      ${this.open ? html`
        <!-- Backdrop -->
        <div
          class="fixed inset-0 z-[1000] bg-black/50 animate-fade-in"
          @click=${this.close}
        ></div>

        <!-- Modal panel: full-screen on mobile, centered card on desktop -->
        <div
          class="fixed inset-x-0 bottom-0 z-[1001] sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4 pointer-events-none"
        >
          <div
            class="bg-base-100 shadow-2xl flex flex-col pointer-events-auto
                   w-full max-h-[85vh] rounded-t-2xl
                   sm:max-w-md sm:rounded-2xl sm:max-h-[80vh]"
            style="overscroll-behavior: contain;"
            @click=${(e: Event) => e.stopPropagation()}
          >
            <!-- Header with search + close -->
            <div class="p-3 border-b border-base-200 shrink-0">
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  class="input input-bordered w-full team-picker-search"
                  placeholder="Search teams..."
                  .value=${this.query}
                  @input=${(e: Event) => { this.query = (e.target as HTMLInputElement).value; }}
                />
                <button
                  type="button"
                  class="btn btn-ghost btn-sm btn-circle shrink-0"
                  @click=${this.close}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="text-xs text-base-content/60 mt-1.5 px-1">
                ${filtered.length} of ${this.teams.length} ${this.teams.length === 1 ? 'team' : 'teams'}
              </div>
            </div>

            <!-- Scrollable list -->
            <ul
              class="overflow-y-auto flex-1"
              style="overscroll-behavior: contain; -webkit-overflow-scrolling: touch;"
            >
              ${filtered.length === 0
                ? html`<li class="px-4 py-6 text-sm text-base-content/60 text-center">No teams match "${this.query}"</li>`
                : filtered.map(team => html`
                    <li>
                      <button
                        type="button"
                        class="w-full text-left px-4 py-3 text-base hover:bg-base-200 active:bg-base-300 border-b border-base-200/50 ${team.id === this.value ? 'bg-primary/10 font-semibold' : ''}"
                        @click=${() => this.select(team.id)}
                      >${team.name}</button>
                    </li>
                  `)}
            </ul>
          </div>
        </div>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'team-picker': TeamPicker;
  }
}
